import { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import RecordPlugin from "wavesurfer.js/dist/plugins/record.esm.js";
import { MAX_RECORDING_SECONDS } from "@/lib/ai/waveformBars";

const MIME_TYPES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];

const supportedMimeType = () => {
  if (typeof MediaRecorder === "undefined") return undefined;
  return MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
};

const primaryColor = (element: HTMLElement) => {
  const raw = getComputedStyle(element).getPropertyValue("--primary").trim();
  return raw ? `hsl(${raw})` : "#3b82f6";
};

export function useWorkoutVoice() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<"idle" | "recording">("idle");
  const [elapsed, setElapsed] = useState(0);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const recordRef = useRef<RecordPlugin | null>(null);
  const discardRef = useRef(false);
  const blobRef = useRef<Promise<Blob | null> | null>(null);
  const resolveBlob = useRef<((blob: Blob | null) => void) | null>(null);
  const startedRef = useRef(false);
  const resolveStart = useRef<(() => void) | null>(null);
  const rejectStart = useRef<((error: unknown) => void) | null>(null);

  const teardown = () => {
    const wavesurfer = wavesurferRef.current;
    wavesurferRef.current = null;
    recordRef.current = null;
    if (!wavesurfer) return;
    try {
      wavesurfer.destroy();
    } catch {
      // The recorder can already be stopped when the bar unmounts.
    }
  };

  useEffect(() => {
    return () => {
      discardRef.current = true;
      const reject = rejectStart.current;
      resolveStart.current = null;
      rejectStart.current = null;
      reject?.(new DOMException("Aborted", "AbortError"));
      teardown();
    };
  }, []);

  useEffect(() => {
    if (phase !== "recording") return;
    const container = containerRef.current;
    if (!container) {
      rejectStart.current?.(new Error("Couldn't start recording"));
      resolveStart.current = null;
      rejectStart.current = null;
      setPhase("idle");
      return;
    }

    let cancelled = false;
    const mimeType = supportedMimeType();
    const color = primaryColor(container);
    const wavesurfer = WaveSurfer.create({
      container,
      height: 24,
      waveColor: color,
      progressColor: color,
      cursorWidth: 0,
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      barHeight: 3,
      barMinHeight: 1,
      interact: false,
      hideScrollbar: true,
    });
    const record = wavesurfer.registerPlugin(
      RecordPlugin.create({
        scrollingWaveform: true,
        scrollingWaveformWindow: 6,
        renderRecordedAudio: false,
        ...(mimeType ? { mimeType } : {}),
      }),
    );
    wavesurferRef.current = wavesurfer;
    recordRef.current = record;
    blobRef.current = new Promise<Blob | null>((resolve) => {
      resolveBlob.current = resolve;
      record.on("record-end", (blob) => resolve(blob));
    });

    record.on("record-start", () => {
      startedRef.current = true;
    });

    record.on("record-progress", (milliseconds) => {
      if (cancelled) return;
      const seconds = Math.min(MAX_RECORDING_SECONDS, milliseconds / 1000);
      setElapsed(seconds);
      if (milliseconds >= MAX_RECORDING_SECONDS * 1000 && record.isRecording()) {
        record.stopRecording();
      }
    });

    void record
      .startRecording()
      .then(() => {
        if (cancelled) return;
        resolveStart.current?.();
        resolveStart.current = null;
        rejectStart.current = null;
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        rejectStart.current?.(error);
        resolveStart.current = null;
        rejectStart.current = null;
        setPhase("idle");
      });

    return () => {
      cancelled = true;
      teardown();
    };
  }, [phase]);

  const start = () => {
    if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      return Promise.reject(new Error("Voice recording isn't supported in this browser"));
    }
    discardRef.current = false;
    startedRef.current = false;
    setElapsed(0);
    setPhase("recording");
    return new Promise<void>((resolve, reject) => {
      resolveStart.current = resolve;
      rejectStart.current = reject;
    });
  };

  const cancel = () => {
    discardRef.current = true;
    const reject = rejectStart.current;
    resolveStart.current = null;
    rejectStart.current = null;
    reject?.(new DOMException("Aborted", "AbortError"));
    if (recordRef.current?.isActive()) recordRef.current.stopRecording();
    else if (!startedRef.current) resolveBlob.current?.(null);
    setPhase("idle");
    setElapsed(0);
  };

  const finish = async (): Promise<Blob | null> => {
    const record = recordRef.current;
    if (record?.isActive()) record.stopRecording();
    else if (!startedRef.current) resolveBlob.current?.(null);
    const blob = await blobRef.current;
    resolveBlob.current = null;
    setPhase("idle");
    setElapsed(0);
    if (discardRef.current || !blob || blob.size === 0) return null;
    return blob;
  };

  return { containerRef, phase, elapsed, start, cancel, finish };
}
